<script setup lang="ts">
import { computed, ref } from 'vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'

import type { BetaPage } from '@/types/beta-dashboard'

const props = defineProps<{
    pages: BetaPage[]
    canPreview?: boolean
    creatingPost?: boolean
}>()

const emit = defineEmits<{
    editHome: []
    createPage: []
    createPost: [pageId: number]
    preview: []
}>()

const selectingPublicationPage = ref(false)

const homePage = computed(() => props.pages.find((page) => page.isHome))
const publicationPages = computed(() => props.pages.filter((page) => page.listPages == 1))

const items = computed(() => {
    if (selectingPublicationPage.value) {
        return [
            {
                id: 'back',
                label: 'Voltar às ações',
                leading: '←',
            },
            ...publicationPages.value.map((page) => ({
                id: `post:${page.id}`,
                label: page.name,
                leading: '▦',
            })),
        ]
    }

    return [
        {
            id: 'edit-home',
            label: 'Editar Home',
            leading: '⌂',
            disabled: !homePage.value,
            hint: !homePage.value ? 'Página inicial indisponível' : undefined,
        },
        {
            id: 'new-page',
            label: 'Nova página',
            leading: '+',
        },
        {
            id: 'new-post',
            label: 'Nova publicação',
            leading: '▦',
            disabled: !publicationPages.value.length || props.creatingPost,
            hint: props.creatingPost
                ? 'Criando publicação…'
                : !publicationPages.value.length
                    ? 'Crie primeiro um Blog ou Portfólio'
                    : undefined,
        },
        {
            id: 'preview',
            label: 'Visualizar site',
            leading: '↗',
            disabled: !props.canPreview,
            hint: !props.canPreview ? 'Endereço indisponível' : undefined,
        },
    ]
})

const finish = (close: () => void) => {
    selectingPublicationPage.value = false
    close()
}

const selectItem = (id: string, close: () => void) => {
    if (id === 'edit-home') {
        emit('editHome')
        finish(close)
        return
    }

    if (id === 'new-page') {
        emit('createPage')
        finish(close)
        return
    }

    if (id === 'new-post') {
        if (publicationPages.value.length === 1) {
            emit('createPost', publicationPages.value[0].id)
            finish(close)
            return
        }

        selectingPublicationPage.value = true
        return
    }

    if (id === 'preview') {
        emit('preview')
        finish(close)
        return
    }

    if (id === 'back') {
        selectingPublicationPage.value = false
        return
    }

    if (!id.startsWith('post:')) return

    const pageId = Number(id.replace('post:', ''))
    const page = publicationPages.value.find((item) => item.id === pageId)
    if (!page) return

    emit('createPost', page.id)
    finish(close)
}
</script>

<template>
    <div class="beta-mobile-quick-actions">
        <Popover v-slot="{ open, close }" class="beta-mobile-quick-actions__popover">
            <PopoverPanel class="beta-mobile-quick-actions__panel">
                <div class="beta-mobile-quick-actions__items">
                    <button
                        v-for="item in items"
                        :key="item.id"
                        type="button"
                        :disabled="item.disabled"
                        :title="item.hint"
                        :aria-label="item.hint ? `${item.label}. ${item.hint}` : item.label"
                        @click="selectItem(item.id, close)"
                    >
                        <span aria-hidden="true">{{ item.leading }}</span>
                        <strong>{{ item.label }}</strong>
                    </button>
                </div>
            </PopoverPanel>

            <PopoverButton
                :class="['beta-mobile-quick-actions__trigger', open && 'is-open']"
                :aria-label="open ? 'Fechar ações rápidas' : 'Abrir ações rápidas'"
                @click="!open && (selectingPublicationPage = false)"
            >
                <span aria-hidden="true">+</span>
            </PopoverButton>
        </Popover>
    </div>
</template>

<style scoped>
.beta-mobile-quick-actions {
    display: none;
}

@media (max-width: 767px) {
    .beta-mobile-quick-actions {
        position: relative;
        z-index: 2;
        display: grid;
        width: 100%;
        min-height: 48px;
        align-self: baseline;
        justify-self: stretch;
        min-width: 0;
        place-items: center;
    }

    .beta-mobile-quick-actions :deep(.beta-mobile-quick-actions__trigger) {
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 2;
        display: grid;
        width: 38px;
        height: 38px;
        place-items: center;
        border: 2px solid var(--beta-accent);
        border-radius: 50%;
        background: #fff;
        color: var(--beta-accent);
        transform: translate(-50%, -50%);
        transition: transform 180ms ease, background 180ms ease;
    }

    .beta-mobile-quick-actions :deep(.beta-mobile-quick-actions__trigger span) {
        font-size: 27px;
        font-weight: 300;
        line-height: 1;
        transition: transform 180ms ease;
    }

    .beta-mobile-quick-actions :deep(.beta-mobile-quick-actions__trigger.is-open) {
        background: var(--beta-accent);
        color: #fff;
    }

    .beta-mobile-quick-actions :deep(.beta-mobile-quick-actions__trigger.is-open span) {
        transform: rotate(45deg);
    }

    .beta-mobile-quick-actions :deep(.beta-mobile-quick-actions__trigger:focus-visible) {
        outline: 3px solid color-mix(in srgb, var(--beta-accent) 30%, white);
        outline-offset: 2px;
    }

    .beta-mobile-quick-actions :deep(.beta-mobile-quick-actions__panel) {
        position: absolute;
        bottom: calc(100% + 25px);
        left: 50%;
        z-index: 1;
        display: grid;
        width: min(196px, calc(100vw - 32px));
        gap: 7px;
        border: 1px solid #dfe6ee;
        border-radius: 18px;
        background: rgba(255, 255, 255, 0.98);
        padding: 10px;
        outline: none;
        transform: translateX(-50%);
        animation: beta-mobile-quick-actions-enter 170ms ease-out;
        backdrop-filter: blur(16px);
    }

    .beta-mobile-quick-actions__panel p {
        margin: 0;
        padding: 2px 5px 4px;
        color: #7a8ba4;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
    }

    .beta-mobile-quick-actions__items {
        display: grid;
        gap: 5px;
    }

    .beta-mobile-quick-actions__items button {
        display: grid;
        min-height: 43px;
        grid-template-columns: 32px minmax(0, 1fr);
        align-items: center;
        gap: 9px;
        border-radius: 12px;
        padding: 5px 9px 5px 5px;
        color: #344a66;
        text-align: left;
        transition: 150ms ease;
    }

    .beta-mobile-quick-actions__items button:hover,
    .beta-mobile-quick-actions__items button:focus-visible {
        background: color-mix(in srgb, var(--beta-accent) 9%, white);
        color: var(--beta-accent);
    }

    .beta-mobile-quick-actions__items button:focus-visible {
        outline: 2px solid color-mix(in srgb, var(--beta-accent) 24%, transparent);
        outline-offset: 1px;
    }

    .beta-mobile-quick-actions__items button:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }

    .beta-mobile-quick-actions__items span {
        display: grid;
        width: 32px;
        height: 32px;
        place-items: center;
        border-radius: 50%;
        background: color-mix(in srgb, var(--beta-accent) 11%, white);
        color: var(--beta-accent);
        font-size: 11px;
        font-weight: 700;
    }

    .beta-mobile-quick-actions__items strong {
        overflow: hidden;
        font-size: 11px;
        font-weight: 700;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

@keyframes beta-mobile-quick-actions-enter {
    from {
        opacity: 0;
        transform: translate(-50%, 10px) scale(0.96);
    }

    to {
        opacity: 1;
        transform: translateX(-50%) scale(1);
    }
}
</style>
